import { useCallback, useEffect, useRef, useState } from 'react';

import {
  composeTranscript, speechErrorMessage, speechRecognitionCtor, transcriptOf,
} from '../utils/speechRecognition';

/**
 * VOICE INTO A TEXT FIELD, over the native Web Speech API.
 *
 * No library: `window.SpeechRecognition || window.webkitSpeechRecognition` is the whole
 * dependency, and a wrapper would add a bundle for an API that is four events.
 *
 * @param onTranscript called with the full text the field should show, as it changes
 * @param onProblem    called with a sentence when the engine stops for a reason worth saying
 * @returns `{ supported, listening, start, stop, toggle }`
 */
export function useSpeechRecognition({ onTranscript, onProblem } = {}) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const baseRef = useRef('');

  // Read ONCE, on mount. Reading it per render would make `supported` a value that could
  // change between a render and the click it gates, and the control would flicker.
  const [Ctor] = useState(() => speechRecognitionCtor());
  const supported = Boolean(Ctor);

  // ⚠ THE CALLBACKS GO THROUGH REFS. They are rebuilt by the parent on every keystroke (they
  // close over the input's text), and the recogniser's handlers are attached ONCE per session —
  // so a handler capturing the callback directly would call a stale one for the rest of the
  // dictation, writing the transcript on top of text from several keystrokes ago.
  const onTranscriptRef = useRef(onTranscript);
  const onProblemRef = useRef(onProblem);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { onProblemRef.current = onProblem; }, [onProblem]);

  const stop = useCallback(() => {
    const engine = recognitionRef.current;
    recognitionRef.current = null;
    setListening(false);
    if (!engine) return;
    try {
      engine.stop();
    } catch {
      // `stop()` on an engine that has already ended throws in some builds. There is nothing
      // to recover: the state is already "not listening", which is what the caller asked for.
    }
  }, []);

  const start = useCallback((baseText = '') => {
    if (!Ctor || recognitionRef.current) return;
    baseRef.current = typeof baseText === 'string' ? baseText : '';

    const engine = new Ctor();
    engine.continuous = false;      // one utterance per press — a command, not a transcript
    engine.interimResults = true;   // the point of the feature: text as it is heard
    engine.maxAlternatives = 1;     // the field shows one string; ranking alternatives is noise

    engine.onresult = (event) => {
      const { final, interim } = transcriptOf(event);
      onTranscriptRef.current?.(composeTranscript(baseRef.current, final + interim));
    };

    engine.onerror = (event) => {
      // ⚠ CLEARS THE STATE. A permission denial fires `error` and then `end`, and without this
      // the pulsing indicator runs for ever over a microphone that is off — which reads as a
      // hung app rather than as a refused permission.
      const message = speechErrorMessage(event?.error);
      if (message) onProblemRef.current?.(message);
      recognitionRef.current = null;
      setListening(false);
    };

    // Fires on silence, on `stop()`, and on the engine deciding the utterance ended. All three
    // mean the same thing to this hook, so there is one handler and no branch.
    engine.onend = () => {
      recognitionRef.current = null;
      setListening(false);
    };

    try {
      engine.start();
      recognitionRef.current = engine;
      setListening(true);
    } catch (e) {
      // `start()` throws synchronously when one is already running.
      onProblemRef.current?.('The microphone could not be started.');
    }
  }, [Ctor]);

  const toggle = useCallback((baseText = '') => {
    if (recognitionRef.current) stop();
    else start(baseText);
  }, [start, stop]);

  // ⚠ STOPS ON UNMOUNT. An orphaned recogniser leaves the browser's own recording indicator
  // lit after the component is gone, which is a privacy signal pointing at nothing.
  useEffect(() => () => {
    const engine = recognitionRef.current;
    recognitionRef.current = null;
    if (engine) {
      try { engine.stop(); } catch { /* already ended */ }
    }
  }, []);

  return { supported, listening, start, stop, toggle };
}

export default useSpeechRecognition;
